'use client'

import { useCallback, useMemo, useState } from 'react'
import { useAuth } from '@/store/hooks'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { usePaginatedPage } from '@/lib/hooks/use-paginated-page'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useUrlPage } from '@/lib/hooks/use-url-page'
import {
    getDepartmentsByOrganization,
    deleteDepartment,
} from '@/lib/api/atendimento/departments'
import { Department } from '@/lib/api/common/interfaces/department.interface'
import { toast } from 'react-toastify'
import {
    MdAdd,
    MdSearch,
    MdEdit,
    MdDelete,
    MdBusiness,
    MdArrowForward,
    MdClose,
    MdStar,
    MdFileDownload,
    MdPhone,
    MdQrCode2,
    MdVerified,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import ConfirmModal from '@/components/confirm-modal'
import PaginationControls from '@/components/pagination/PaginationControls'
import { ImportDataModal } from '@/components/import/ImportDataModal'
import { ShareableModule } from '@/lib/api/share/share-api'
import { formatPhoneNumber } from '@/lib/form-state/util'

export default function DepartmentsListPage() {
    const { user, isPartner } = useAuth()
    const navigate = useNavigate()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions(
        'atendimento',
        'sectors'
    )
    const [searchTerm, setSearchTerm] = useState('')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [departmentToDelete, setDepartmentToDelete] = useState<number | null>(
        null
    )
    const [deleting, setDeleting] = useState(false)
    const [showImportModal, setShowImportModal] = useState(false)
    const [urlPage, setUrlPage] = useUrlPage()

    const debouncedSearch = useDebounce(searchTerm, 300)
    const organizationId = user?.organizationId ?? user?.company?.organizationId

    const filters = useMemo(
        () => ({ search: debouncedSearch.trim() || undefined }),
        [debouncedSearch]
    )

    const fetchFn = useCallback(
        (params: typeof filters & { page: number; limit: number }) => {
            return getDepartmentsByOrganization(organizationId!, params)
        },
        [organizationId]
    )

    const {
        items: displayedDepartments,
        loading,
        page,
        limit,
        total,
        setPage,
        reload,
    } = usePaginatedPage<Department, typeof filters>({
        fetchFn,
        filters,
        limit: 15,
        enabled: !!organizationId,
        page: urlPage,
        onPageChange: setUrlPage,
    })

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault()
    }

    const handleDelete = (departmentId: number) => {
        setDepartmentToDelete(departmentId)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!departmentToDelete) return

        setDeleting(true)
        try {
            const response = await deleteDepartment(departmentToDelete)
            if (response.success) {
                toast.success('Departamento excluído com sucesso')
                reload()
                setShowDeleteModal(false)
                setDepartmentToDelete(null)
            } else {
                toast.error(response.error || 'Erro ao excluir departamento')
            }
        } catch (error) {
            toast.error('Erro ao excluir departamento')
        } finally {
            setDeleting(false)
        }
    }

    if (loading && displayedDepartments.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando setores...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent to-accent/70 flex items-center justify-center shadow-lg">
                        <MdBusiness className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            Departamentos
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {total} {total === 1 ? 'setor' : 'setores'}{' '}
                            cadastrados
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <div className="flex items-center gap-2">
                        <button
                            className="btn btn-outline btn-secondary gap-2"
                            onClick={() => setShowImportModal(true)}
                        >
                            <MdFileDownload className="text-xl" />
                            <span className="hidden sm:inline">Importar</span>
                        </button>
                        <button
                            className="btn btn-secondary gap-2"
                            onClick={() => navigate('/atendimento/sectors/new')}
                        >
                            <MdAdd className="text-xl" />
                            <span className="hidden sm:inline">Adicionar</span>
                        </button>
                    </div>
                )}
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                <form
                    onSubmit={handleSearch}
                    className="flex items-center gap-2"
                >
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por nome ou descrição..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                </form>
            </div>

            {displayedDepartments.length === 0 && !loading ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdBusiness className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            {searchTerm
                                ? 'Nenhum departamento encontrado'
                                : 'Nenhum departamento cadastrado'}
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            {searchTerm
                                ? 'Tente ajustar sua busca'
                                : 'Comece adicionando setores'}
                        </p>
                        {!searchTerm && canCreate && (
                            <button
                                onClick={() =>
                                    navigate('/atendimento/sectors/new')
                                }
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-xl" />
                                Adicionar primeiro department
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {displayedDepartments.map(department => {
                        const isWeb =
                            department.whatsappProvider === 'WHATSAPP_WEB'

                        return (
                            /* O card inteiro abre a edição: obrigar a mirar no
                               lápis é atrito puro numa tela que se navega o
                               tempo todo. Os botões param a propagação para não
                               herdar esse clique. */
                            <div
                                key={department.id}
                                role={canUpdate ? 'button' : undefined}
                                tabIndex={canUpdate ? 0 : undefined}
                                onClick={
                                    canUpdate
                                        ? () =>
                                              navigate(
                                                  `/atendimento/sectors/edit/${department.id}`
                                              )
                                        : undefined
                                }
                                onKeyDown={
                                    canUpdate
                                        ? e => {
                                              if (
                                                  e.key === 'Enter' ||
                                                  e.key === ' '
                                              ) {
                                                  e.preventDefault()
                                                  navigate(
                                                      `/atendimento/sectors/edit/${department.id}`
                                                  )
                                              }
                                          }
                                        : undefined
                                }
                                className={`group flex flex-col bg-base-100 rounded-xl border border-base-300/60 p-5 transition-all duration-200 ${
                                    canUpdate
                                        ? 'cursor-pointer hover:border-secondary/60 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary'
                                        : ''
                                }`}
                            >
                                <div className="flex items-start gap-3">
                                    <div className="w-12 h-12 flex-shrink-0 rounded-xl bg-secondary/10 flex items-center justify-center border border-secondary/20">
                                        <MdBusiness className="text-2xl text-secondary" />
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-bold text-base-content truncate">
                                            {department.name}
                                        </h3>
                                        <p className="text-sm text-base-content/60 line-clamp-2 mt-0.5 min-h-[2.5rem]">
                                            {department.description ||
                                                'Sem descrição'}
                                        </p>
                                    </div>

                                    {canUpdate && (
                                        <MdArrowForward className="text-lg text-base-content/20 group-hover:text-secondary transition-colors flex-shrink-0" />
                                    )}
                                </div>

                                <div className="flex flex-wrap items-center gap-1.5 mt-4">
                                    <span
                                        className={`badge badge-sm gap-1 ${
                                            isWeb
                                                ? 'badge-success badge-outline'
                                                : 'badge-ghost'
                                        }`}
                                    >
                                        {isWeb ? <MdQrCode2 /> : <MdVerified />}
                                        {isWeb ? 'WhatsApp Web' : 'API oficial'}
                                    </span>

                                    {department.isDefaultForNotifications && (
                                        <span className="badge badge-sm badge-warning gap-1">
                                            <MdStar />
                                            Notificações
                                        </span>
                                    )}
                                </div>

                                <div className="flex items-center justify-between gap-2 mt-4 pt-3 border-t border-base-300/50">
                                    <span className="flex items-center gap-1.5 text-sm text-base-content/70 min-w-0">
                                        <MdPhone className="text-base flex-shrink-0" />
                                        <span className="truncate">
                                            {department.whatsappNumber
                                                ? formatPhoneNumber(
                                                      department.whatsappNumber
                                                  )
                                                : isWeb
                                                  ? 'Aguardando pareamento'
                                                  : 'Sem número'}
                                        </span>
                                    </span>

                                    {(canUpdate || canDelete) && (
                                        <div className="flex items-center gap-1 flex-shrink-0">
                                            {canUpdate && (
                                                <button
                                                    onClick={e => {
                                                        e.stopPropagation()
                                                        navigate(
                                                            `/atendimento/sectors/edit/${department.id}`
                                                        )
                                                    }}
                                                    className="btn btn-xs btn-ghost btn-square"
                                                    title="Editar"
                                                >
                                                    <MdEdit className="text-base" />
                                                </button>
                                            )}
                                            {canDelete && (
                                                <button
                                                    onClick={e => {
                                                        e.stopPropagation()
                                                        handleDelete(
                                                            department.id
                                                        )
                                                    }}
                                                    className="btn btn-xs btn-ghost btn-square text-error hover:bg-error/10"
                                                    title="Excluir"
                                                >
                                                    <MdDelete className="text-base" />
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}

            {displayedDepartments.length > 0 && total > limit && (
                <PaginationControls
                    page={page}
                    limit={limit}
                    total={total}
                    loading={loading}
                    onPageChange={setPage}
                    label="setor"
                />
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setDepartmentToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir departamento"
                message="Tem certeza de que deseja excluir este departamento? Esta ação não pode ser desfeita."
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
            />

            <ImportDataModal
                isOpen={showImportModal}
                onClose={() => setShowImportModal(false)}
                module={ShareableModule.DEPARTMENT}
                moduleLabel="Departamentos"
                fetchItems={async (scopeId: number) => {
                    const response = await getDepartmentsByOrganization(scopeId)
                    return response.success ? response.result : []
                }}
                renderItem={(item: Department) => (
                    <div>
                        <p className="text-sm font-medium truncate">
                            {item.name}
                        </p>
                        {item.description && (
                            <p className="text-xs text-base-content/60 truncate">
                                {item.description}
                            </p>
                        )}
                    </div>
                )}
                getItemId={(item: Department) => item.id}
                getItemName={(item: Department) => item.name}
                onImportComplete={reload}
            />
        </div>
    )
}
